import { useQuery } from '@apollo/react-hooks';
import { GET_APPLICATION } from './GET_Application';
import {
  type GetApplication,
  type ApplicationData,
  type ApplicationPageItemData,
} from './type';
import {
  ADDITIONAL_DOCS_TYPE,
  PROGRAMME_TYPE,
  PAGE_TEXT,
  PublicationTypesOptions,
} from '../../../components';
import {
  type AdditionalDocFormData,
  type AcademicQualificationFormData,
  type DistinctiveScholarship,
  type LanguageQualificationFormData,
  type LanguageQualificationResult,
  type OtherTestFormData,
  type OtherTestResult,
  type Mark,
  type PublicationPayload,
  type ProgrammeChoiceFormData,
  checkInvalidDate,
} from '../../../graphql';
import { type SelectOption } from '../../../../../components';
import { type ApplicationRemarkType } from '../../../../Nomination/graphql';

export const getApplication = (id: string): GetApplication => {
  const {
    data: application,
    loading,
    error,
    refetch,
  } = useQuery(GET_APPLICATION, {
    variables: { id, applicantId: id },
    skip: !id,
    fetchPolicy: 'no-cache',
  });

  const {
    applicationNo,
    applicant,
    chiefSupervisor,
    department,
    programmeChoice,
    academicQualifications,
    languageQualifications,
    otherTests,
    publications,
    merits,
    academicReferees,
    researchExperiences,
    employments,
    professionalQualifications,
    trainings,
    activities,
    additionalDocuments: initialAdditionalDocData = [],
    status,
    hkpfsInformation,
    distinctiveScholarships = [],
    remarks: returnRemarks,
    proofDocuments,
    declaration,
  } = application?.application ?? {};

  const applicantId: string = applicant?._id;

  const programmeChoiceFormData: ProgrammeChoiceFormData = {
    programmeChoiceId: programmeChoice?._id ?? '',
    entryYear: programmeChoice?.entryYear ?? '',
    studyMode: programmeChoice?.studyMode ?? '',
    department: programmeChoice?.department ?? '',
    programme: programmeChoice?.programme ?? '',
    programmeType: programmeChoice?.programmeType ?? '',
    isDualOrCollaborative:
      programmeChoice?.programmeType ===
        PROGRAMME_TYPE.COLLABORATIVE_PHD_DEGREE_PROGRAMME ||
      programmeChoice?.programmeType ===
        PROGRAMME_TYPE.DUAL_PHD_DEGREE_PROGRAMME,
    partner: {
      university: programmeChoice?.partner?.university ?? '',
      proposedChiefSupervisor:
        programmeChoice?.partner?.proposedChiefSupervisor ?? '',

      universityCategory: programmeChoice?.partner?.universityCategory ?? '',
      isPolyUHomeUniversity:
        programmeChoice?.partner?.isPolyUHomeUniversity ?? false,
      physicalLocation: programmeChoice?.partner?.physicalLocation ?? '',
    },
    researchArea: programmeChoice?.researchArea ?? '',
    proposedChiefSupervisor: (programmeChoice?.department as boolean) // Check if applicant has programme choice and choose proposedChiefSupervisor
      ? (programmeChoice?.proposedChiefSupervisor ??
        PAGE_TEXT.PROGRAMME_CHOICE.OPTIONS.NO_INDICATION)
      : '',
    researchAreaBackup: programmeChoice?.researchAreaBackup ?? '',
    proposedChiefSupervisorBackup:
      programmeChoice?.proposedChiefSupervisorBackup ?? '',
    acceptNoResearchIndicator:
      programmeChoice?.acceptNoResearchIndicator ?? null,
  };

  const academicQualificationsData: AcademicQualificationFormData[] =
    academicQualifications?.map((academicQualification: any) => {
      const {
        presentIndicator = false,
        gpaIndicator = false,
        rankIndicator = false,
        academicCredentialIndicator = false,
        degreeCertIndicator = false,
        degreeLevel = '',
        programmeName = '',
        from = null,
        to = null,
        studyMode = '',
        currentStudyYear = undefined,
        institution,
        instructionMedium = '',
        otherInstructionMedium = '',
        studyField = '',
        qualificationMeans = '',
        qualificationStatus = '',
        awardDate = null,
        awardClassification = '',
        expectUpdateDate = null,
        withdrawDate = null,
        officialMark = null,
        outOfGpa = null,
        markConversion,
        convertGPARuleId = undefined,
        marks = [],
        officialClassRanking = null,
        outOfRank = null,
        academicOnlineVerificationCode = '',
        degreeOnlineVerificationCode = '',
        certificates = [],
        transcripts = [],
        markExplanations = [],
        classRankProofs = [],
        otherDocuments = [],
      } = academicQualification ?? {};
      let processedCourses: Mark[] = [...marks];

      if (markConversion?.digitToLetterRule?.name === 'Average Mark') {
        processedCourses = marks?.map((course: Mark) => ({
          ...course,
          credit: null,
        }));
      }
      return {
        academicQualificationId: academicQualification?._id,
        presentIndicator: presentIndicator ?? false,
        from: checkInvalidDate(from) ? null : from,
        to: checkInvalidDate(to) ? null : to,
        currentStudyYear,
        studyMode,
        institutionName: institution?.name ?? '',
        institutionCountry: institution?.country ?? '',
        instructionMedium,
        otherInstructionMedium,
        studyField,
        degreeLevel,
        qualificationMeans,
        programmeName,
        qualificationStatus,
        awardDate: checkInvalidDate(awardDate) ? null : awardDate,
        awardClassification:
          awardClassification === null ? '' : awardClassification,
        officialMark,
        outOfGpa,
        marks: processedCourses,
        convertGPARuleId,
        convertedMark: markConversion?.convertedMark ?? '',
        letterToGPARule: markConversion?.letterToGPARule?.name ?? '',
        digitToLetterRule: markConversion?.digitToLetterRule?.name ?? '',
        expectUpdateDate: checkInvalidDate(expectUpdateDate)
          ? null
          : expectUpdateDate,
        withdrawDate: checkInvalidDate(withdrawDate) ? null : withdrawDate,
        academicOnlineVerificationCode,
        degreeOnlineVerificationCode,
        officialClassRanking,
        outOfRank,
        certificates,
        transcripts,
        markExplanations,
        classRankProofs,
        otherDocuments,
        gpaIndicator,
        rankIndicator,
        academicCredentialIndicator,
        degreeCertIndicator,
      };
    }) ?? [];

  const {
    needEnglishTest,
    result = {} as LanguageQualificationResult,
    resultAnnouncedIndicator,
    testDate,
    expectedResultUpdateDate,
    testType = '',
    referenceNo = '',
    supportingDocuments = [],
    _id,
  } = languageQualifications?.[0] ?? {};

  const languageQualificationData: LanguageQualificationFormData = {
    testType: testType ?? '',
    testDate: checkInvalidDate(testDate) ? null : String(testDate),
    expectedResultUpdateDate: checkInvalidDate(expectedResultUpdateDate)
      ? null
      : String(expectedResultUpdateDate),
    resultAnnouncedIndicator: resultAnnouncedIndicator ?? false,
    ...((resultAnnouncedIndicator as boolean) ? result : {}),
    referenceNo,
    supportingDocuments,
    needEnglishTest,
    languageQualificationId: _id,
  };

  const otherTestData: OtherTestFormData[] =
    otherTests?.map((item: any) => {
      const {
        result = {} as OtherTestResult,
        resultAnnouncedIndicator,
        testDate,
        expectedResultUpdateDate,
        testType,
        referenceNo,
        supportingDocuments = [],
        _id,
      } = item ?? {};

      return {
        ...result,
        otherTestId: _id,
        testDate: checkInvalidDate(testDate) ? null : String(testDate),
        expectedResultUpdateDate: checkInvalidDate(expectedResultUpdateDate)
          ? null
          : String(expectedResultUpdateDate),
        testType,
        referenceNo,
        supportingDocuments,
        resultAnnouncedIndicator: resultAnnouncedIndicator ?? false,
      };
    }) ?? [];

  const publicationsData: PublicationPayload[] =
    publications?.map((item: any) => {
      const {
        _id,
        publicationType,
        publicationAuthors,
        publicationTitle,
        publicationName,
        applicantRole,
        specificRole,
        publicationStatus,
        publicationDate,
        issuePageArticelNo,
        peerReviewedIndicator,
        citationInfo,
        publicationUrl,
        supportingDocuments = [],
      } = item ?? {};

      return {
        publicationId: _id ?? '',
        publicationType: (publicationType as boolean)
          ? !PublicationTypesOptions.find(
              item => item.value === publicationType,
            )
            ? 'OTHER_PUBLICATION'
            : publicationType
          : '',
        otherType: publicationType,
        publicationAuthors,
        publicationTitle,
        publicationName,
        applicantRole,
        specificRole,
        publicationStatus,
        publicationDate: checkInvalidDate(publicationDate)
          ? null
          : publicationDate,
        issuePageArticelNo,
        peerReviewedIndicator: peerReviewedIndicator ?? false,
        citationInfo,
        publicationUrl,
        supportingDocuments,
      };
    }) ?? [];

  const meritIds: string[] =
    merits?.map((item: ApplicationPageItemData) => item?._id ?? '') ?? [];
  const academicRefereeIds: string[] =
    academicReferees?.map((item: ApplicationPageItemData) => item?._id ?? '') ??
    [];
  const researchExperienceIds: string[] =
    researchExperiences?.map(
      (item: ApplicationPageItemData) => item?._id ?? '',
    ) ?? [];
  const employmentIds: string[] =
    employments?.map((item: ApplicationPageItemData) => item?._id ?? '') ?? [];
  const professionalQualificationIds: string[] =
    professionalQualifications?.map(
      (item: ApplicationPageItemData) => item?._id ?? '',
    ) ?? [];
  const trainingIds: string[] =
    trainings?.map((item: ApplicationPageItemData) => item?._id ?? '') ?? [];
  const activitiesIds: string[] =
    activities?.map((item: ApplicationPageItemData) => item?._id ?? '') ?? [];

  const curriculumVitaeData =
    initialAdditionalDocData?.filter(
      (doc: any) => doc?.documentType === ADDITIONAL_DOCS_TYPE.CURRICULUM,
    ) ?? [];

  const researchProposalData =
    initialAdditionalDocData?.filter(
      (doc: any) => doc?.documentType === ADDITIONAL_DOCS_TYPE.RESEARCH,
    ) ?? [];

  const studyPlanData =
    initialAdditionalDocData?.filter(
      (doc: any) => doc?.documentType === ADDITIONAL_DOCS_TYPE.STUDY_PLAN,
    ) ?? [];

  const personalStatementData =
    initialAdditionalDocData?.filter(
      (doc: any) =>
        doc?.documentType === ADDITIONAL_DOCS_TYPE.PERSONAL_STATEMENT,
    ) ?? [];

  const identificationDocument =
    initialAdditionalDocData?.filter(
      (doc: any) =>
        doc?.documentType === ADDITIONAL_DOCS_TYPE.IDENTIFICATION_DOCUMENT,
    ) ?? [];

  const requiredDocTypes = [
    ADDITIONAL_DOCS_TYPE.CURRICULUM,
    ADDITIONAL_DOCS_TYPE.IDENTIFICATION_DOCUMENT,
    ADDITIONAL_DOCS_TYPE.STUDY_PLAN,
    ADDITIONAL_DOCS_TYPE.PERSONAL_STATEMENT,
    ADDITIONAL_DOCS_TYPE.RESEARCH,
  ];
  const additionalDocumentsData = initialAdditionalDocData?.filter(
    (doc: any) => !requiredDocTypes.includes(doc?.documentType),
  );

  const proofDocumentsData: AdditionalDocFormData[] =
    proofDocuments?.map((doc: any) => ({
      _id: doc?._id ?? '',
      documentType: doc?.documentType ?? '',
      supportingDocuments: doc?.supportingDocuments ?? [],
    })) ?? [];

  const initialDistinctiveScholarships: DistinctiveScholarship[] = [
    {
      fundingSource: '',
      reason: '',
      files: [],
    },
    {
      fundingSource: '',
      reason: '',
      files: [],
    },
  ];

  const distinctiveScholarshipsData: DistinctiveScholarship[] =
    initialDistinctiveScholarships?.map(
      (scholarship, index) => distinctiveScholarships?.[index] ?? scholarship,
    );

  const applicationData: ApplicationData = {
    applicationNo,
    applicant: applicantId,
    chiefSupervisor,
    department,
    programmeChoice: programmeChoiceFormData,
    academicQualifications: academicQualificationsData,
    languageQualifications: languageQualificationData,
    otherTests: otherTestData,
    publications: publicationsData,
    merits: meritIds,
    academicReferees: academicRefereeIds,
    researchExperiences: researchExperienceIds,
    employments: employmentIds,
    professionalQualifications: professionalQualificationIds,
    trainings: trainingIds,
    activities: activitiesIds,
    additionalDocuments: additionalDocumentsData,
    curriculumVitae: curriculumVitaeData,
    researchProposal: researchProposalData,
    identificationDocument,
    studyPlan: studyPlanData,
    personalStatement: personalStatementData,
    hkpfsInformation: {
      HKPFSReferenceNumber: hkpfsInformation?.HKPFSReferenceNumber ?? '',
    },
    distinctiveScholarships: distinctiveScholarshipsData,
    proofDocuments: proofDocumentsData,
    declaration: {
      personalType: declaration?.personalType ?? undefined,
      confirmation: declaration?.confirmation ?? false,
      informationAccuracy: declaration?.informationAccuracy ?? false,
      engagement: declaration?.engagement ?? undefined,
    },
    status,
  };

  const remarks: ApplicationRemarkType[] = returnRemarks ?? [];

  const countryList: SelectOption[] =
    application?.programmeChoiceMetadata?.countryList ?? [];

  const universityList: SelectOption[] =
    application?.programmeChoiceMetadata?.universityList ?? [];

  const conditions = application?.meetCondition?.conditions ?? [];
  const meetConditions = {
    conditions: conditions
      .filter((item: any) => item?.passed !== true)
      .map((item: any) => item.item),
    status: application?.meetCondition?.status ?? '',
  };

  return {
    applicationData,
    remarks,
    countryList,
    universityList,
    meetConditions,
    loading,
    error,
    refetch,
  };
};
